import { createSelector } from '@ngrx/store';
import {
  IBusinessPartner,
  IFeatureConfig,
  IGlobalConfig,
  ILoanProfile,
  IPolicy,
  IPolicyConfig,
  IProcessProfile,
  IProfileParams
} from '@partners/api';
import { TCatalogOption } from '@partners/catalog';
import { IFlatStage, IRoute, IStage } from '@partners/navigation';
import { TProcessMode } from '@partners/types';
import { IRootState } from '../../root';
import { IErrorsState, INavigationState, IShellState, IShellUiState } from './shell.state';

export class ShellSelectors {
  static readonly shellState = (state: IRootState): IShellState => {
    return state.shell as IShellState;
  };

  /**
   * application-level ui
   */
  static readonly uiState = createSelector(ShellSelectors.shellState, (appState: IShellState) => {
    return appState?.uiState as IShellUiState;
  });

  /**
   * application-level mode
   */
  static readonly errors = createSelector(ShellSelectors.shellState, (appState: IShellState) => {
    return appState?.errors as IErrorsState;
  });

  /**
   * profile selectors
   */
  static readonly profile = createSelector(ShellSelectors.shellState, (appState: IShellState) => {
    return appState?.profile as IProcessProfile;
  });

  // static readonly process = createSelector(ShellSelectors.profile, (profileState: IProcessProfile) => {
  //   return profileState?.processId as TProcessId;
  // });

  static readonly mode = createSelector(ShellSelectors.profile, (profileState: IProcessProfile) => {
    return profileState?.mode as TProcessMode;
  });

  static readonly params = createSelector(ShellSelectors.profile, (profileState: IProcessProfile) => {
    return profileState?.params as IProfileParams;
  });

  static readonly process = createSelector(ShellSelectors.params, (profileParams: IProfileParams) => {
    return profileParams?.lobCode;
  });

  static readonly policy = createSelector(ShellSelectors.profile, (profileState: IProcessProfile) => {
    return profileState?.policy as IPolicy;
  });
  static readonly token = createSelector(ShellSelectors.params, (params: IProfileParams) => {
    return params?.token;
  });

  static readonly config = createSelector(ShellSelectors.policy, (policy: IPolicy) => {
    return policy?.config as IPolicyConfig;
  });

  static readonly loanReasons = createSelector(ShellSelectors.policy, (policy: IPolicy) => {
    return policy?.loanReasons as TCatalogOption[];
  });

  static readonly loanProfiles = createSelector(ShellSelectors.policy, (policy: IPolicy) => {
    return policy?.loanProfiles as ILoanProfile[];
  });

  static readonly globalConfig = createSelector(ShellSelectors.config, (config: IPolicyConfig) => {
    return config?.global as IGlobalConfig;
  });

  static readonly featuresConfig = createSelector(ShellSelectors.config, (config: IPolicyConfig) => {
    return config?.modules as IFeatureConfig[];
  });

  static readonly businessPartner = createSelector(ShellSelectors.policy, (policy: IPolicy) => {
    return policy?.businessPartner as IBusinessPartner;
  });

  /**
   * navigation selectors
   */
  static readonly navigation = createSelector(ShellSelectors.shellState, (appState: IShellState) => {
    return appState?.navigation as INavigationState;
  });

  static readonly stages = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.stages as IStage[];
  });

  static readonly flatStages = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.flatStages as IFlatStage[];
  });

  static readonly previousStage = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.previousStage as IStage;
  });

  static readonly currentStage = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.currentStage as IStage;
  });

  static readonly nextStage = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.nextStage as IStage;
  });

  static readonly anonymousStages = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.anonymousStages as Partial<IStage>[];
  });

  static readonly previousRoute = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.previousRoute as IRoute;
  });

  static readonly currentRoute = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.currentRoute as IRoute;
  });

  static readonly nextRoute = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.nextRoute as IRoute;
  });

  static readonly totalPristineStages = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.totalPristine as number;
  });

  static readonly clientProcessId = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.clientProcessId;
  });

  static readonly totalCompleteStages = createSelector(ShellSelectors.navigation, (navState: INavigationState) => {
    return navState?.totalComplete as number;
  });
}
